// Drag-and-drop reorder math.
//
// `overIdx` is the index at which to INSERT (a position between rows,
// not a row index). After removing the row at `from`, every row past
// `from` shifts down by one — so we adjust the target.
//
// Examples (route = [A, B, C, D]):
//   from=0, overIdx=2 (drop between B and C) → after remove: [B, C, D],
//     re-insert above C means target index 1 → final [B, A, C, D]
//   from=2, overIdx=0 → target 0 → [C, A, B, D]
//   from=1, overIdx=1 (no-op) → from === overIdx → skip
//   from=1, overIdx=2 (drop just below self) → from + 1 === overIdx → skip
//   from=0, overIdx=4 (append) → target 3 (length-1 of new) → [B, C, D, A]

/**
 * Compute the destination index for a reorder operation.
 *
 * Returns null when the operation is a no-op (drop onto self or just
 * past self). Otherwise returns the index `move(from, target)` should
 * use.
 *
 * @param length    Current array length.
 * @param from      Index being moved.
 * @param overIdx   Insertion position (0..length inclusive).
 */
export function computeReorderTarget(length: number, from: number, overIdx: number): number | null {
  if (from < 0 || from >= length) return null;
  if (overIdx < 0 || overIdx > length) return null;
  if (from === overIdx) return null;
  if (from + 1 === overIdx) return null;
  return overIdx > from ? overIdx - 1 : overIdx;
}

/** In-place reorder for testing the math end-to-end. */
export function applyReorder<T>(arr: T[], from: number, target: number): T[] {
  const copy = arr.slice();
  const [item] = copy.splice(from, 1);
  copy.splice(target, 0, item);
  return copy;
}
